/**
 * Character-track rows: a bar drawn from text glyphs, `[=========|-----]`, on the monospace grid
 * the tables already use. Adapted from mdxcn's `graph-bullet`, `graph-rank` and `graph-meter`
 * (MIT, see apps/web/THIRD_PARTY_NOTICES.md) without `motion`, MDX children or the framed title,
 * and coloured with our own tokens: fill in gold, fill past a met target in sage, the target mark
 * in paper, empty ticks in the line colour.
 */

type Tone = "gold" | "sage" | "steel" | "danger";

export type TrackProps = {
  value: number;
  /** scale end; defaults to the larger of value and target */
  max?: number;
  /** draws a `|` at the target and turns the fill sage once the value reaches it */
  target?: number;
  /** a target the value must stay under (cost caps): the fill turns danger past it */
  ceiling?: boolean;
  ticks?: number;
  tone?: Tone;
  label: string;
};

export function Track({
  value,
  max,
  target,
  ceiling = false,
  ticks = 24,
  tone = "gold",
  label,
}: TrackProps) {
  const peak = Math.max(max ?? Math.max(value, target ?? 0), Number.EPSILON);
  const filled = Math.min(ticks, Math.round((Math.max(value, 0) / peak) * ticks));
  const mark =
    target === undefined
      ? null
      : Math.min(ticks - 1, Math.max(0, Math.round((Math.max(target, 0) / peak) * ticks)));
  const met = target !== undefined && (ceiling ? value <= target : value >= target);
  const fillTone: Tone = target === undefined ? tone : met ? "sage" : ceiling ? "danger" : tone;
  return (
    <span className="track" role="img" aria-label={label}>
      <span className="track-edge" aria-hidden="true">
        [
      </span>
      <span className="track-body" aria-hidden="true">
        {Array.from({ length: ticks }, (_, i) => {
          const isMark = mark !== null && i === mark;
          const on = i < filled;
          const cls = isMark ? "track-mark" : on ? `track-on track-${fillTone}` : "track-off";
          return (
            // biome-ignore lint/suspicious/noArrayIndexKey: ticks are positional and fixed in number
            <span key={i} className={cls}>
              {isMark ? "|" : on ? "=" : "-"}
            </span>
          );
        })}
      </span>
      <span className="track-edge" aria-hidden="true">
        ]
      </span>
    </span>
  );
}

export type TrackRow = {
  label: string;
  value: number;
  /** shown on the right; defaults to the formatted value */
  display?: string;
  target?: number;
  max?: number;
  ceiling?: boolean;
};

/** Label, track and value in three columns: rank lists (no target) and bullet rows (with one). */
export function TrackList({
  rows,
  max,
  ticks,
  tone,
  testId,
}: {
  rows: TrackRow[];
  /** shared scale for rank lists; defaults to the largest value */
  max?: number;
  ticks?: number;
  tone?: Tone;
  testId?: string;
}) {
  const peak = max ?? Math.max(...rows.map((r) => r.max ?? r.value), 1);
  return (
    <ul className="track-list" data-testid={testId}>
      {rows.map((r) => {
        const shown = r.display ?? r.value.toLocaleString("en-US");
        return (
          <li key={r.label} className="track-row">
            <span className="track-label" title={r.label}>
              {r.label}
            </span>
            <Track
              value={r.value}
              max={r.max ?? peak}
              target={r.target}
              ceiling={r.ceiling}
              ticks={ticks}
              tone={tone}
              label={`${r.label} ${shown}`}
            />
            <span className="track-value num">{shown}</span>
          </li>
        );
      })}
    </ul>
  );
}
